import React from 'react';
import { Link } from 'react-router-dom';
import {
  Circle,
  GoogleMap,
  Marker,
  withGoogleMap,
  withScriptjs,
} from 'react-google-maps';
import { lifeTaggerApi } from '../api/lifetaggerApi';
import './LocationSignalCreationWorkspace.css';

const steps = ['Name', 'Place', 'Boundary', 'Connect', 'Review'];
const pageSize = 8;

const MapPickerCanvas = withScriptjs(
  withGoogleMap(({ coordinates, radius, onMove }) => (
    <GoogleMap
      defaultZoom={16}
      center={{ lat: coordinates.latitude, lng: coordinates.longitude }}
      defaultOptions={{ mapTypeControl: false, streetViewControl: false }}
    >
      {radius ? (
        <Circle
          center={{ lat: coordinates.latitude, lng: coordinates.longitude }}
          radius={radius}
          options={{
            fillColor: '#b94935',
            fillOpacity: 0.12,
            strokeColor: '#b94935',
            strokeOpacity: 0.75,
            strokeWeight: 2,
          }}
        />
      ) : null}
      <Marker
        draggable
        position={{ lat: coordinates.latitude, lng: coordinates.longitude }}
        onDragEnd={(event) =>
          onMove({
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng(),
          })
        }
      />
    </GoogleMap>
  ))
);

function WorkspaceHeader() {
  return (
    <header className="location-topbar">
      <Link className="location-brand" to="/objects">
        <img src="/images/lifeTagger_logo.png" alt="" />
        <span>LifeTagger</span>
      </Link>
      <nav className="location-nav">
        <div className="location-switcher">
          <Link to="/objects">Collection</Link>
          <Link className="active" to="/signals">
            Signals
          </Link>
        </div>
        <span>Activity</span>
        <span>Settings</span>
      </nav>
      <div className="location-account">
        <span>⌕</span>
        <span>♢</span>
        <b>LT</b>
      </div>
    </header>
  );
}

function Progress({ current }) {
  return (
    <div className="location-progress" aria-label={`Step ${current} of 5`}>
      {steps.map((label, index) => (
        <div className={index + 1 <= current ? 'complete' : ''} key={label}>
          <span>{index + 1}</span>
          <strong>{label}</strong>
          {index < steps.length - 1 && <i />}
        </div>
      ))}
    </div>
  );
}

const thumbFor = (item) =>
  item.content?.find((content) => content.type === 'image' && content.url)?.url;

export default function LocationSignalCreationWorkspace({ history, location }) {
  const initialFence =
    new URLSearchParams(location.search).get('mode') === 'geofence';
  const [step, setStep] = React.useState(1);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [addressQuery, setAddressQuery] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [coordinates, setCoordinates] = React.useState(null);
  const [boundary, setBoundary] = React.useState(
    initialFence ? 'geofence' : 'point'
  );
  const [radius, setRadius] = React.useState(150);
  const [objects, setObjects] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [objectPage, setObjectPage] = React.useState(1);
  const [objectSearch, setObjectSearch] = React.useState('');
  const [loadingObjects, setLoadingObjects] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [created, setCreated] = React.useState(null);
  const [permissionNotice, setPermissionNotice] = React.useState(false);
  const dirty = Boolean(
    name || description || addressQuery || selectedIds.size
  );

  React.useEffect(() => {
    if (step !== 4 || objects.length) return;
    setLoadingObjects(true);
    lifeTaggerApi
      .listLifeObjectsPage({ page: 1, limit: 100 })
      .then((result) => setObjects(result.items || []))
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoadingObjects(false));
  }, [step, objects.length]);

  React.useEffect(() => {
    const warn = (event) => {
      if (!dirty || created) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, created]);

  const leave = () => {
    if (
      !dirty ||
      window.confirm('Leave creation? Your unsaved changes will be lost.')
    )
      history.push('/signals');
  };

  const findPlace = async () => {
    if (!addressQuery.trim())
      return setError('Enter an address or place to search.');
    setSearching(true);
    setError('');
    try {
      const endpoint = new URL(
        'https://maps.googleapis.com/maps/api/geocode/json'
      );
      endpoint.searchParams.set('address', addressQuery.trim());
      endpoint.searchParams.set(
        'key',
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );
      const response = await fetch(endpoint.toString());
      const data = await response.json();
      const result = data.results?.[0];
      if (!result)
        throw new Error(
          'No places found. Try a broader place name, city, or street address.'
        );
      setAddress(result.formatted_address);
      setAddressQuery(result.formatted_address);
      setCoordinates({
        latitude: result.geometry.location.lat,
        longitude: result.geometry.location.lng,
      });
    } catch (requestError) {
      setError(
        requestError.message || 'Places search is unavailable. Try again.'
      );
    } finally {
      setSearching(false);
    }
  };

  const useCurrentLocation = () => {
    if (!permissionNotice) {
      setPermissionNotice(true);
      return;
    }
    if (!navigator.geolocation)
      return setError('Current location is not available in this browser.');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setCoordinates({
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
        setAddress('Current location');
        setAddressQuery('Current location');
        setError('');
      },
      () =>
        setError(
          'Location permission was denied. You can still search for a place above.'
        )
    );
  };

  const validRadius =
    boundary === 'point' ||
    (Number.isInteger(Number(radius)) && radius >= 25 && radius <= 5000);
  const filteredObjects = objects.filter((item) =>
    item.name?.toLowerCase().includes(objectSearch.trim().toLowerCase())
  );
  const objectPages = Math.max(1, Math.ceil(filteredObjects.length / pageSize));
  const visibleObjects = filteredObjects.slice(
    (objectPage - 1) * pageSize,
    objectPage * pageSize
  );

  const next = () => {
    setError('');
    if (step === 1 && !name.trim())
      return setError('Give this place signal a clear name.');
    if (step === 2 && !coordinates)
      return setError('Choose and confirm a real place before continuing.');
    if (step === 3 && !validRadius)
      return setError('Enter a whole number from 25 to 5,000 meters.');
    setStep((value) => Math.min(5, value + 1));
  };

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const input = {
        name: name.trim(),
        type: boundary === 'geofence' ? 'geo-fence' : 'geo-point',
        coordinates,
        metadata: { description: description.trim(), address },
        ...(boundary === 'geofence' ? { radiusMeters: Number(radius) } : {}),
      };
      const anchor = await lifeTaggerApi.createAnchor(input);
      const results = await Promise.allSettled(
        [...selectedIds].map((lifeObjectId) =>
          lifeTaggerApi.linkAnchor(lifeObjectId, anchor.id)
        )
      );
      const failedConnections = results.filter(
        (result) => result.status === 'rejected'
      ).length;
      setCreated({
        ...anchor,
        ...input,
        connectedCount: selectedIds.size - failedConnections,
      });
      if (failedConnections)
        setError(
          `${failedConnections} LifeObject connection could not be saved. The location signal was still created.`
        );
    } catch (requestError) {
      setError(
        requestError.message || 'The location signal could not be created.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (created)
    return (
      <div className="location-page">
        <WorkspaceHeader />
        <main className="location-success">
          <span className="location-success-mark">✓</span>
          <p className="location-crumb">Signals / Location created</p>
          <h1>Your place can now reveal LifeObjects</h1>
          <p>
            <strong>{created.name}</strong> is active at {address}. It is
            connected to {created.connectedCount} LifeObject
            {created.connectedCount === 1 ? '' : 's'}.
          </p>
          {error && <div className="location-error">{error}</div>}
          <SignalSummary
            name={created.name}
            address={address}
            boundary={boundary}
            radius={radius}
          />
          <div className="location-success-actions">
            <button onClick={() => history.push('/signals')}>
              View in Signals
            </button>
            <button
              className="primary"
              onClick={() => window.location.reload()}
            >
              Create another
            </button>
          </div>
        </main>
      </div>
    );

  return (
    <div className="location-page">
      <WorkspaceHeader />
      <main className="location-workspace">
        <p className="location-crumb">Signals / New location signal</p>
        <h1>
          {
            [
              'Name this place signal',
              'Confirm the real place',
              'Choose how arrival is detected',
              'Connect the experience',
              'Review this place signal',
            ][step - 1]
          }
        </h1>
        <p className="location-intro">
          {
            [
              'Give collaborators a clear name. The signal’s geographic data is added next.',
              'Search with Google, then verify and refine the selected marker on the map.',
              'Use a precise point or a practical geofence around the confirmed place.',
              'Choose which LifeObjects visitors can discover here.',
              'Confirm the experience before making this location active.',
            ][step - 1]
          }
        </p>
        <Progress current={step} />
        {error && (
          <div className="location-error" role="alert">
            {error}
          </div>
        )}

        {step === 1 && (
          <div className="location-columns">
            <section className="location-card">
              <h2>Signal identity</h2>
              <label>
                Signal name
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Botanical Garden Arrival"
                />
                <small>Visible to collaborators and in Spatial View.</small>
              </label>
              <label>
                Description (optional)
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What should collaborators know about this place?"
                />
                <small>Visitors will not see this description.</small>
              </label>
            </section>
            <aside className="location-note green">
              <b>⌖</b>
              <h3>Geographic by design</h3>
              <p>
                Detection uses the saved place and optional radius—not a public
                code or operator-entered identifier.
              </p>
            </aside>
          </div>
        )}

        {step === 2 && (
          <div className="location-columns">
            <section className="location-card">
              <label>
                Search address or place
                <div className="location-search">
                  <input
                    value={addressQuery}
                    onChange={(e) => setAddressQuery(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && (e.preventDefault(), findPlace())
                    }
                    placeholder="Search a place, address, or landmark"
                  />
                  <button onClick={findPlace} disabled={searching}>
                    {searching ? 'Searching…' : 'Search'}
                  </button>
                </div>
                <small>
                  Choosing a result stores the resolved address and map
                  position.
                </small>
              </label>
              {coordinates && (
                <div className="location-place-result">
                  <span>⌖</span>
                  <div>
                    <strong>{name || 'Selected place'}</strong>
                    <small>{address}</small>
                  </div>
                  <b>Selected ✓</b>
                </div>
              )}
              {coordinates ? (
                <LocationMap
                  coordinates={coordinates}
                  radius={boundary === 'geofence' ? Number(radius) : 0}
                  onMove={setCoordinates}
                />
              ) : (
                <div className="location-map-empty">
                  <span>⌖</span>
                  <strong>Your selected place will appear here</strong>
                  <small>Search above to load the map.</small>
                </div>
              )}
              <div className="location-map-actions">
                <small>
                  The confirmed address and map position are saved internally.
                </small>
                <button onClick={useCurrentLocation}>
                  Use current location
                </button>
              </div>
              {permissionNotice && !coordinates && (
                <div className="location-warning">
                  Your browser will ask permission to use your current location.{' '}
                  <button onClick={useCurrentLocation}>Continue</button>
                </div>
              )}
            </section>
            <aside className="location-note">
              <h2>Finding the right place</h2>
              <p>
                Search a venue, landmark, or street address. Drag the marker to
                refine the exact entrance after choosing a result.
              </p>
            </aside>
          </div>
        )}

        {step === 3 && (
          <div className="location-columns">
            <section className="location-card">
              <div className="location-segmented">
                <button
                  className={boundary === 'point' ? 'active' : ''}
                  onClick={() => setBoundary('point')}
                >
                  Point
                </button>
                <button
                  className={boundary === 'geofence' ? 'active' : ''}
                  onClick={() => setBoundary('geofence')}
                >
                  Geofence
                </button>
              </div>
              <LocationMap
                coordinates={coordinates}
                radius={boundary === 'geofence' ? Number(radius) : 0}
                onMove={setCoordinates}
              />
              {boundary === 'geofence' && (
                <label>
                  Radius in meters
                  <input
                    type="number"
                    min="25"
                    max="5000"
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                  />
                  <small>
                    Recommended: 50–300 m. Allow for GPS drift without covering
                    unrelated entrances.
                  </small>
                  <div className="radius-presets">
                    {[50, 100, 150, 300].map((value) => (
                      <button
                        className={radius === value ? 'active' : ''}
                        onClick={() => setRadius(value)}
                        key={value}
                      >
                        {value} m
                      </button>
                    ))}
                  </div>
                </label>
              )}
            </section>
            <aside>
              <div className="location-note dark">
                <h2>How it will be detected</h2>
                <p>
                  {boundary === 'point'
                    ? 'A visitor’s permitted device location at this saved place can reveal connected LifeObjects.'
                    : `A visitor’s permitted device location entering the ${radius} m area can reveal connected LifeObjects.`}
                </p>
              </div>
              {!validRadius && (
                <div className="location-note danger">
                  <strong>Radius needs attention</strong>
                  <p>Enter a whole number from 25 to 5,000 meters.</p>
                </div>
              )}
            </aside>
          </div>
        )}

        {step === 4 && (
          <section className="location-card location-connect">
            <div className="location-connect-heading">
              <div>
                <h2>Choose LifeObjects</h2>
                <p>You can change these connections later.</p>
              </div>
              <span>Connected ({selectedIds.size})</span>
            </div>
            <input
              className="location-object-search"
              value={objectSearch}
              onChange={(e) => {
                setObjectSearch(e.target.value);
                setObjectPage(1);
              }}
              placeholder="Search LifeObjects…"
            />
            {loadingObjects ? (
              <div className="location-loading">Loading LifeObjects…</div>
            ) : (
              visibleObjects.map((item) => {
                const selected = selectedIds.has(item.id);
                return (
                  <button
                    className={`location-object-row ${
                      selected ? 'selected' : ''
                    }`}
                    onClick={() =>
                      setSelectedIds((current) => {
                        const nextSet = new Set(current);
                        selected
                          ? nextSet.delete(item.id)
                          : nextSet.add(item.id);
                        return nextSet;
                      })
                    }
                    key={item.id}
                  >
                    <span className="location-object-thumb">
                      {thumbFor(item) ? (
                        <img src={thumbFor(item)} alt="" />
                      ) : (
                        '◇'
                      )}
                    </span>
                    <strong>{item.name}</strong>
                    <span>
                      {item.lifecycle?.status === 'active'
                        ? 'Published'
                        : 'Draft'}
                    </span>
                    <b>{selected ? 'Connected ✓' : 'Connect'}</b>
                  </button>
                );
              })
            )}
            <div className="location-object-pagination">
              <span>
                Showing {visibleObjects.length} of {filteredObjects.length}
              </span>
              <div>
                <button
                  disabled={objectPage === 1}
                  onClick={() => setObjectPage(objectPage - 1)}
                >
                  Previous
                </button>
                <strong>
                  {objectPage} of {objectPages}
                </strong>
                <button
                  disabled={objectPage === objectPages}
                  onClick={() => setObjectPage(objectPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </section>
        )}

        {step === 5 && (
          <div className="location-columns">
            <SignalSummary
              name={name}
              address={address}
              boundary={boundary}
              radius={radius}
              description={description}
            />
            <aside className="location-note green">
              <h2>Ready to create</h2>
              <p>
                This will make the location active and connect{' '}
                {selectedIds.size} LifeObject{selectedIds.size === 1 ? '' : 's'}
                .
              </p>
            </aside>
          </div>
        )}

        <footer className="location-footer">
          <button onClick={step === 1 ? leave : () => setStep(step - 1)}>
            Back
          </button>
          <div>
            <small>Changes stay in this draft until creation</small>
            <button
              className="primary"
              disabled={saving}
              onClick={step === 5 ? create : next}
            >
              {saving
                ? 'Creating…'
                : step === 5
                ? 'Create location signal'
                : `${steps[step]} →`}
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
}

function LocationMap({ coordinates, radius, onMove }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!coordinates || !apiKey)
    return <div className="location-map-empty">Map unavailable</div>;
  return (
    <MapPickerCanvas
      coordinates={coordinates}
      radius={radius}
      onMove={onMove}
      googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=3.exp&libraries=geometry,places`}
      loadingElement={<div className="location-map-loading" />}
      containerElement={<div className="location-map" />}
      mapElement={<div style={{ height: '100%' }} />}
    />
  );
}

function SignalSummary({ name, address, boundary, radius, description }) {
  return (
    <section className="location-card location-summary">
      <h2>{name}</h2>
      {description && <p>{description}</p>}
      <dl>
        <div>
          <dt>Place</dt>
          <dd>{address}</dd>
        </div>
        <div>
          <dt>Detection</dt>
          <dd>
            {boundary === 'geofence'
              ? `${radius} m arrival area`
              : 'Precise place'}
          </dd>
        </div>
      </dl>
    </section>
  );
}
